import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '@/services/auth';
import type { LoginFormData, UserAttributes, NewPasswordFormData } from '@/types/auth';
import type { AuthUser } from 'aws-amplify/auth';

interface AuthState {
  user: AuthUser | null;
  userAttributes: UserAttributes | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresNewPassword: boolean;
  authStep: 'LOGIN' | 'OTP' | 'NEW_PASSWORD' | 'AUTHENTICATED';
  email: string;
}

const initialState: AuthState = {
  user: null,
  userAttributes: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  requiresNewPassword: false,
  authStep: 'LOGIN',
  email: '',
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginFormData) => {
    const result = await authService.signIn(credentials);
    
    if (result.nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { 
        requiresNewPassword: true, 
        user: null, 
        userAttributes: null,
        nextStep: result.nextStep,
        email: credentials.email
      };
    }
    
    if (result.nextStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      return {
        requiresNewPassword: false,
        user: null,
        userAttributes: null,
        nextStep: result.nextStep,
        email: credentials.email
      };
    }
    
    if (!result.isSignedIn) {
      throw new Error('Login fallito');
    }
    
    const user = await authService.getCurrentUser();
    return { 
      user, 
      userAttributes: result.userAttributes,
      requiresNewPassword: false,
      nextStep: result.nextStep,
      email: credentials.email
    };
  }
);

export const confirmOtp = createAsyncThunk(
  'auth/confirmOtp',
  async (otpData: { code: string }) => {
    const result = await authService.confirmSignInWithOtp(otpData);
    
    if (!result.isSignedIn) {
      throw new Error('OTP verification failed');
    }
    
    const user = await authService.getCurrentUser();
    return { user, userAttributes: result.userAttributes };
  }
);

export const confirmNewPassword = createAsyncThunk(
  'auth/confirmNewPassword',
  async (passwordData: NewPasswordFormData) => {
    await authService.confirmSignInWithNewPassword(passwordData.password);
    const user = await authService.getCurrentUser();
    const userAttributes = await authService.getUserAttributes();
    return { user, userAttributes };
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  await authService.signOut();
  dispatch(logoutSuccess());
});

export const checkAuth = createAsyncThunk('auth/check', async () => {
  const user = await authService.getCurrentUser();
  if (user) {
    const userAttributes = await authService.getUserAttributes();
    return { user, userAttributes };
  }
  return null;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuthStep: (state) => {
      state.authStep = 'LOGIN';
      state.requiresNewPassword = false;
      state.error = null;
      state.email = '';
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.userAttributes = null;
      state.isAuthenticated = false;
      state.requiresNewPassword = false;
      state.authStep = 'LOGIN';
      state.email = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.email = action.payload.email || '';
        
        if (action.payload.requiresNewPassword) {
          state.requiresNewPassword = true;
          state.authStep = 'NEW_PASSWORD';
          state.isAuthenticated = false;
        } else if (action.payload.nextStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          state.authStep = 'OTP';
          state.isAuthenticated = false;
        } else {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.userAttributes = action.payload.userAttributes || null;
          state.authStep = 'AUTHENTICATED';
          state.requiresNewPassword = false;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Error during login';
        state.authStep = 'LOGIN';
      })
      // Confirm OTP
      .addCase(confirmOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.userAttributes = action.payload.userAttributes || null;
        state.authStep = 'AUTHENTICATED';
        state.requiresNewPassword = false;
      })
      .addCase(confirmOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Invalid OTP code';
        state.authStep = 'OTP';
      })
      // Confirm New Password
      .addCase(confirmNewPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmNewPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.userAttributes = action.payload.userAttributes || null;
        state.authStep = 'AUTHENTICATED';
        state.requiresNewPassword = false;
      })
      .addCase(confirmNewPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Errore durante il cambio password';
        state.authStep = 'NEW_PASSWORD';
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.userAttributes = action.payload.userAttributes || null;
          state.isAuthenticated = true;
          state.authStep = 'AUTHENTICATED';
          state.requiresNewPassword = false;
        } else {
          state.user = null;
          state.userAttributes = null;
          state.isAuthenticated = false;
          state.authStep = 'LOGIN';
          state.requiresNewPassword = false;
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.userAttributes = null;
        state.isAuthenticated = false;
        state.authStep = 'LOGIN';
        state.requiresNewPassword = false;
      });
  },
});

export const { clearError, resetAuthStep, logoutSuccess } = authSlice.actions;
export default authSlice.reducer; 